<script setup>
import { initializeWindowEvents } from '@/composables/helpers/layouts/window'
import { updateGlobals } from '@/composables/data/updateGlobals'

const { data: global } = await useAsyncData('all-global-data', () => $fetch('/api/global-data'))
const { data: airtable } = await useAsyncData('all-air-table', () => $fetch('/api/v1/all'));

await updateGlobals(unref(global), unref(airtable))
initializeWindowEvents()

const props = defineProps({
    error: Object
})

const handleError = () => clearError({ redirect: '/' })

const image = {
    src: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2F236b8d4bb02c4a3ebbe3b4430e2d224c',
    alt: 'A photo of dog, laying down, exhausted.'
}

const message = ref({
    headline: 'Oops!',
    copy: 'Sorry, something went wrong and we couldn’t process that request.'
})

const button = ref({
    label: 'Go home',
    title: 'Navigate to the home page',
    link: '/'
})

const page = ref({
    data: {
        title: `${props.error.statusCode} error page`
    }
})

if (props.error.statusCode === 404) {
    message.value.headline = 'Woof!'
    message.value.copy = 'Sorry, something went wrong and we can’t find the page you’re looking for.'
}

</script>

<template>
    <div class="page-wrapper error-page" data-model="error-page" key="error-page">
        <GlobalHeader />
        <main id="main" key="page" class="mess-page" tabindex="-1">
            <section class="block error-block treat-as-hero">
                <div class="container">
                    <div class="split-hide">
                        <div class="image">
                            <MessImage :image="image" />
                        </div>
                        <div class='copy-box'>
                            <MessHtml v-if="message.headline" class="headline fs-100" tag="h2" :html="message.headline" />
                            <MessHtml v-if="message.copy" class="copy fs-20" :html="message.copy" />
                            <ProjectButton @click="handleError" class="button" :button="button" outline  />
                        </div>
                    </div>
                </div>
            </section>
        </main>
        <GlobalFooter :pageTitle="page.data.title" :page="page" />
    </div>
</template>

<style lang="scss">

.error-page {
    background-color: var(--orange);
}

.error-page .mess-page {
    background-color: var(--orange);
    background-image: url('@/assets/img/backgrounds/bubble-orange-4.png');
    background-position: left bottom;
    background-repeat: no-repeat;
}

.error-block {
    background-image: url('@/assets/img/backgrounds/bubble-orange-5.png');
    background-position: right top;
    background-repeat: no-repeat;
    
    .container {
        color: white;
        margin-top: calc(var(--header-height));
        border-radius: var(--radius);
        background-color: var(--orange);
        
        @media (min-width: $tablet) {
            background-image: url('@/assets/img/backgrounds/bubble-orange-6-small.png');
            background-position: left top;
            background-repeat: no-repeat;
        }
    }

    .image {
        overflow: hidden;
        border-radius: var(--radius) var(--radius) 0 0;

        img {
            object-fit: cover;
        }

        @media (min-width: $tablet) {
            border-radius: 0 var(--radius) var(--radius) 0;         
            .mess-image:before {
                @include fakebox;
                background-image: url('@/assets/img/backgrounds/bubble-orange-5-small.png');
                background-position: right bottom;
                background-repeat: no-repeat;  
                z-index: 1;
            }
        }
    }

    .split-hide {
        display: flex;
        flex-direction: column;
        gap: var(--box-padding-lr-m);
        max-height: 31.8125rem;
        
        @media (min-width: $tablet) {            
            flex-direction: row;
            .copy-box {
                order: -1;
            }
        }
    }

    .copy-box {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: start;
        padding: var(--box-padding-tb-m) var(--box-padding-lr-m);

        .copy {
            margin-bottom: var(--box-padding-tb);
        }
    }
}

</style>